import React, { useState, useEffect } from 'react';
import { Trophy, BarChart3, TrendingUp, Loader, AlertCircle } from 'lucide-react';
import API from '../../api/API';
import './AClassifiche.css';

const AClassifiche = () => {
  const [edizioneSelezionata, setEdizioneSelezionata] = useState(null);
  const [classifiche, setClassifiche] = useState(null);
  const [squadre, setSquadre] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedEdition = localStorage.getItem('selectedEdition');
    if (storedEdition) {
      const edizione = JSON.parse(storedEdition);
      setEdizioneSelezionata(edizione);
      fetchClassificheData(edizione.id);

      
    }
  }, []);

  const fetchClassificheData = async (edizioneId) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(edizioneId)
      const [classificheResponse, squadreResponse] = await Promise.all([
        API.planning.classificaGironi(edizioneId),
        API.torneo.squadre(edizioneId)
      ]);

      if (classificheResponse.isSuccess && squadreResponse.isSuccess) {
        setClassifiche(classificheResponse.data.classifica);
        
        // Create a map of squadraId to team data for quick lookup
        const squadreMap = {};
        squadreResponse.data.forEach(squadra => {
          squadreMap[squadra.id] = squadra;
        });
        setSquadre(squadreMap);
      } else {
        setError(classificheResponse.errorMsg || squadreResponse.errorMsg || 'Errore nel caricamento delle classifiche');
      }
    } catch (err) {
      setError(err.message || 'Errore nel caricamento delle classifiche');
    } finally {
      setLoading(false);
    }
  };

  // Transform API data to component format
  const getClassificheFormatted = () => {
    if (!classifiche) return [];
    
    return Object.keys(classifiche)
      .sort((a, b) => {
        // Classify each entry into a priority group
        // Priority: 1=girone+number, 2=classifica+number, 3=girone+letter, 4=classifica+letter
        const classify = (str) => {
          const lowerStr = str.toLowerCase();
          const gironeNumMatch = lowerStr.match(/^girone[_\s]*(\d+)$/);
          const classificaNumMatch = lowerStr.match(/^classifica[_\s]*(\d+)$/);
          const gironeLetterMatch = lowerStr.match(/^girone[_\s]*([a-z]+)$/);
          const classificaLetterMatch = lowerStr.match(/^classifica[_\s]*([a-z]+)$/);

          if (gironeNumMatch) {
            return { priority: 1, value: parseInt(gironeNumMatch[1]), isNumber: true };
          } else if (classificaNumMatch) {
            return { priority: 2, value: parseInt(classificaNumMatch[1]), isNumber: true };
          } else if (gironeLetterMatch) {
            return { priority: 3, value: gironeLetterMatch[1], isNumber: false };
          } else if (classificaLetterMatch) {
            return { priority: 4, value: classificaLetterMatch[1], isNumber: false };
          } else {
            return { priority: 5, value: str.toLowerCase(), isNumber: false };
          }
        };

        const classA = classify(a);
        const classB = classify(b);

        // First compare by priority group
        if (classA.priority !== classB.priority) {
          return classA.priority - classB.priority;
        }

        // Within same group, sort by value
        if (classA.isNumber) {
          return classA.value - classB.value;
        } else {
          return classA.value.localeCompare(classB.value);
        }
      })
      .map(faseName => ({
        nomeFase: faseName,
        squadre: classifiche[faseName].items
          .map(item => ({
            squadraId: item.squadraId,
            nomeSquadra: squadre[item.squadraId]?.nome || `Squadra ${item.squadraId}`,
            punti: item.punti,
            meteFatte: item.meteFatte,
            meteSubite: item.meteSubite,
            pieni: item.pieni,
            vuoti: item.vuoti,
            falli: item.falli,
            partiteGiocate: item.partiteGiocate
          }))
      }));
  };

  const LoadingSpinner = () => (
    <div className="admin-loading">
      <Loader className="admin-loading-icon" />
      <h3 className="admin-loading-title">Caricamento classifiche...</h3>
      <p className="admin-loading-text">Attendere prego</p>
    </div>
  );

  const ErrorMessage = () => (
    <div className="admin-error">
      <AlertCircle className="admin-error-icon" />
      <h3 className="admin-error-title">Errore nel caricamento</h3>
      <p className="admin-error-text">{error}</p>
      {edizioneSelezionata && (
        <button 
          onClick={() => fetchClassificheData(edizioneSelezionata.id, edizioneSelezionata.torneoId)}
          className="admin-retry-button"
        >
          Riprova
        </button>
      )}
    </div>
  );

  const ClassificaCard = ({ fase }) => {
    return (
      <div className="classifica-card">
        <div className="classifica-header">
          <div className="classifica-title-container">
            <Trophy className="classifica-icon" />
            <h3 className="classifica-title">{fase.nomeFase}</h3>
            <span className="squadre-count">({fase.squadre.length} squadre)</span>
          </div>
        </div>

        <div className="classifica-table-container">
          <table className="classifica-table">
            <thead>
              <tr>
                <th className="pos-header">Pos</th>
                <th className="squadra-header">Squadra</th>
                <th className="stat-header">Punti</th>
                <th className="stat-header">Mete F.</th>
                <th className="stat-header">Mete S.</th>
                <th className="stat-header">Pieni</th>
                <th className="stat-header">Vuoti</th>
                <th className="stat-header">Falli</th>
                <th className="stat-header">Partite</th>
              </tr>
            </thead>
            <tbody>
              {fase.squadre.map((squadra, index) => (
                <tr key={squadra.squadraId || squadra.nomeSquadra} className={`table-row ${index < 3 ? `podium-${index + 1}` : ''}`}>
                  <td className="pos-cell">
                    <div className={`position-badge ${index < 3 ? `pos-${index + 1}` : 'pos-other'}`}>
                      {index + 1}
                    </div>
                  </td>
                  <td className="squadra-cell">
                    <span className="squadra-name">{squadra.nomeSquadra}</span>
                  </td>
                  <td className="stat-cell punti-cell">
                    <strong>{squadra.punti}</strong>
                  </td>
                  <td className="stat-cell">{squadra.meteFatte}</td>
                  <td className="stat-cell">{squadra.meteSubite}</td>
                  <td className="stat-cell">{squadra.pieni}</td>
                  <td className="stat-cell">{squadra.vuoti}</td>
                  <td className="stat-cell">{squadra.falli}</td>
                  <td className="stat-cell">{squadra.partiteGiocate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-container">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <BarChart3 className="admin-header-icon" />
          <div>
            <h1 className="admin-title">Classifiche</h1>
            <p className="admin-subtitle">Risultati aggregati delle partite per fase</p>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage />
      ) : !edizioneSelezionata ? (
        <div className="admin-no-selection">
          <TrendingUp className="no-selection-icon" />
          <h3>Nessuna edizione selezionata</h3>
          <p>Seleziona un'edizione dalla pagina Tornei per visualizzare le classifiche</p>
        </div>
      ) : !classifiche ? (
        <div className="admin-no-selection">
          <TrendingUp className="no-selection-icon" />
          <h3>Nessun dato disponibile</h3>
          <p>Non ci sono classifiche disponibili per questa edizione</p>
        </div>
      ) : (
        <div className="classifiche-content">

          <div className="classifiche-list">
            {getClassificheFormatted().map((fase) => (
              <ClassificaCard key={fase.nomeFase} fase={fase} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AClassifiche;